import styled from 'styled-components';
import {
  StyledTextInputProps,
  StyledCategoryButtonProps,
  StyledSuggestionItemProps,
  StyledSuggestionContentProps,
  StyledIconProps,
} from './types';

export const Container = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const InputContainer = styled.div`
  position: relative;
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  gap: 0.75rem;
  justify-content: space-between;
`;

export const SegmentsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
`;

export const TextInput = styled.input<StyledTextInputProps>`
  outline: none;
  border: none;
  min-width: 60px;
  width: ${(props) => (props.$isOnly ? '100%' : 'auto')};
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: transparent;
  font-size: 0.875rem;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.disabled};
  }
`;

export const ContentEditableInput = styled.div`
  outline: none;
  border: none;
  width: 100%;
  min-height: 1.5rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: transparent;
  font-size: 0.875rem;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;

  &:empty:before {
    content: attr(data-placeholder);
    color: ${({ theme }) => theme.colors.text.disabled};
    pointer-events: none;
  }

  &:focus {
    outline: none;
  }

  .document-reference {
    display: inline;
    color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => `${theme.colors.primary}14`};
    border-radius: ${({ theme }) => theme.borderRadius.xs};
    padding: 0.125rem 0.375rem;
    margin: 0 0.125rem;
    font-weight: 500;
    cursor: pointer;
    position: relative;
    user-select: none;
    white-space: nowrap;
    vertical-align: baseline;

    &:hover {
      background-color: ${({ theme }) => `${theme.colors.primary}26`};
      color: ${({ theme }) => theme.colors.primary};
    }

    &[contenteditable='false'] {
      -webkit-user-modify: read-only;
      -moz-user-modify: read-only;
      user-modify: read-only;
    }
  }

  .person-reference {
    display: inline;
    color: ${({ theme }) => theme.colors.success};
    background-color: ${({ theme }) => `${theme.colors.success}14`};
    border-radius: ${({ theme }) => theme.borderRadius.xs};
    padding: 0.125rem 0.375rem;
    margin: 0 0.125rem;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    vertical-align: baseline;

    &:hover {
      background-color: ${({ theme }) => `${theme.colors.success}26`};
      color: ${({ theme }) => theme.colors.success};
    }

    &[contenteditable='false'] {
      -webkit-user-modify: read-only;
      -moz-user-modify: read-only;
      user-modify: read-only;
    }
  }
`;

export const DocumentTag = styled.span`
  display: inline;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => `${theme.colors.primary}80`};
  text-underline-offset: 2px;
  position: relative;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration-color: ${({ theme }) => theme.colors.primary};
  }

  &::before {
    content: '[';
    color: ${({ theme }) => theme.colors.primary};
  }

  &::after {
    content: ']';
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const PersonTag = styled.span`
  display: inline;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.success};
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => `${theme.colors.success}80`};
  text-underline-offset: 2px;

  &:hover {
    color: ${({ theme }) => theme.colors.success};
    text-decoration-color: ${({ theme }) => theme.colors.success};
  }
`;

export const RemoveButton = styled.button`
  margin-left: 0.25rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  opacity: 0;
  transition: opacity 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
    opacity: 1;
  }

  &:focus {
    outline: none;
  }
`;

export const InlineReferenceWrapper = styled.span`
  display: inline;
  position: relative;

  &:hover ${RemoveButton} {
    opacity: 1;
  }
`;

export const SendButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const SendButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DropdownContainer = styled.div<{ $positionAbove?: boolean }>`
  position: absolute;
  ${({ $positionAbove }) =>
    $positionAbove
      ? `
    bottom: calc(100% + 0.5rem);
  `
      : `
    top: calc(100% + 0.5rem);
  `}
  left: 0;
  right: 0;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 500px;
  display: flex;
  flex-direction: column;
`;

export const SearchContainer = styled.div`
  padding: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0.375rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.primary}40`};
  }
`;

export const CategoryContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  overflow-x: auto;
`;

export const CategoryButton = styled.button<StyledCategoryButtonProps>`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: ${(props) =>
    props.$active ? props.theme.colors.primary : props.theme.colors.text.secondary};
  border-bottom: 2px solid
    ${(props) => (props.$active ? props.theme.colors.primary : 'transparent')};
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const SuggestionsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

export const SuggestionItem = styled.div<StyledSuggestionItemProps>`
  cursor: pointer;
  background-color: ${(props) =>
    props.$selected ? `${props.theme.colors.primary}14` : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}0d`};
  }
`;

export const SuggestionItemContent = styled.div<StyledSuggestionContentProps>`
  display: flex;
  align-items: ${(props) =>
    props.$type === 'command'
      ? 'center'
      : props.$type === 'person'
      ? 'center'
      : 'start'};
  padding: 0.5rem;
  gap: 0.5rem;
`;

export const IconWrapper = styled.div`
  flex-shrink: 0;
`;

export const Icon = styled.i<StyledIconProps>`
  font-size: ${(props) => (props.$large ? '1.5rem' : '1rem')};
  color: ${({ theme }) => theme.colors.text.disabled};
  margin-right: ${(props) => (props.$marginRight ? '0.5rem' : '0')};
`;

export const DocumentInfo = styled.div`
  margin-left: 0.75rem;
  flex: 1;
  min-width: 0;
`;

export const DocumentTitle = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DocumentMeta = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const PersonInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;

export const PersonAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

export const PersonName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MirrorDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  white-space: pre;
  font: inherit;
  padding: 0;
  margin: 0;
`;
